import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Check, Copy, Mail } from "lucide-react";
import toast from "react-hot-toast";

import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { getDgiConfigTrail } from "@/components/onboarding/OnboardingTrail";

export interface OnboardingProviderRequestNoticeStepProps {
  /** Used to personalize the template body / subject. */
  companyDisplayName: string;
  /** RUT for the subject — falls back to a placeholder if missing. */
  tenantRut: string | null;
  /** Advance the wizard (to viewStep 3 — DGI access). */
  onContinue: () => void | Promise<void>;
  /** Step back into the situation-selection view. */
  onBack: () => void;
  isSaving?: boolean;
  /** When true, the trail shows a 4th "CFE anteriores" step. Defaults to
   *  true here because this notice only renders for the already_issuer +
   *  migration path. */
  isMigratingCfes?: boolean;
}

/**
 * Notice step shown after a user picks "Ya soy emisor electrónico" on the
 * Situation step. Surfaces the provider-request email template so they can
 * fire it off to their current biller before continuing — by the time
 * they finish DGI + cert setup, the certificate / passphrase / CFE export
 * / CAE files are usually already in their inbox.
 *
 * The same template body lives in ProviderRequestTemplateDialog (the
 * dialog-form variant used from /onboarding/CertificateStep). We render
 * it as a full step here so it's load-bearing in the flow instead of an
 * easy-to-miss link.
 */
export function OnboardingProviderRequestNoticeStep({
  companyDisplayName,
  tenantRut,
  onContinue,
  onBack,
  isSaving = false,
  isMigratingCfes = true,
}: OnboardingProviderRequestNoticeStepProps): JSX.Element {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<"subject" | "body" | null>(
    null,
  );

  const company =
    companyDisplayName.trim() ||
    t(
      "onboarding.providerRequestTemplate.fallbackCompany",
      "[Nombre de tu empresa]",
    );
  const rut =
    tenantRut?.trim() ||
    t("onboarding.providerRequestTemplate.fallbackRut", "[RUT]");

  const subject = t("onboarding.providerRequestTemplate.subject", {
    defaultValue: "Solicitud de certificado digital y CFE — {{company}}",
    company,
  });
  const body = t("onboarding.providerRequestTemplate.body", {
    defaultValue:
      "Hola,\n\nEstamos migrando nuestra facturación electrónica a un nuevo proveedor y necesitamos los siguientes archivos para completar la transición de {{company}} (RUT {{rut}}):\n\n1. El certificado digital (.pfx o .p12) emitido para nuestra empresa.\n2. La contraseña del certificado.\n3. Una exportación de todos los comprobantes fiscales electrónicos (CFE) generados con nuestra cuenta.\n4. Los CAE (Constancia de Autorización de Emisión) correspondientes.\n\nQuedamos atentos para cualquier coordinación.\n\nMuchas gracias.",
    company,
    rut,
  });
  const subjectLabel = t(
    "onboarding.providerRequestTemplate.subjectLabel",
    "Asunto",
  );
  const bodyLabel = t(
    "onboarding.providerRequestTemplate.bodyLabel",
    "Mensaje",
  );

  async function handleCopyField(
    field: "subject" | "body",
    value: string,
  ): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      toast.success(
        t(
          field === "subject"
            ? "onboarding.providerRequestTemplate.subjectCopiedToast"
            : "onboarding.providerRequestTemplate.bodyCopiedToast",
          field === "subject" ? "Asunto copiado" : "Mensaje copiado",
        ),
      );
      window.setTimeout(() => setCopiedField(null), 2500);
    } catch {
      toast.error(
        t(
          "onboarding.providerRequestTemplate.copyErrorToast",
          "No se pudo copiar — seleccioná y copiá manualmente",
        ),
      );
    }
  }

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={1}
            total={isMigratingCfes ? 4 : 3}
            label={t(
              "onboarding.providerRequestNotice.tag",
              "Pedile a tu proveedor",
            )}
            flow={t(
              "onboarding.situationStep.flow",
              "Configuración DGI",
            )}
          />
          <h1 className="fx-onb-h1">
            {t(
              "onboarding.providerRequestNotice.h1Lead",
              "Pedile a tu proveedor actual",
            )}{" "}
            <span className="ital">
              {t(
                "onboarding.providerRequestNotice.h1Accent",
                "el certificado y tus CFE.",
              )}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.providerRequestNotice.lead",
              "Mandales este mensaje ahora — no hace falta esperar la respuesta para seguir configurando Fiscalo. Cuando lleguen los archivos, los subís en pocos minutos y migrás todo de una.",
            )}
          </p>

          {/* Subject card — separate from the body so the user can copy
              either independently. The card background lifts off the page
              paper (--fx-paper) with --fx-ink-900 dark fill, evoking an
              inbox preview so the email content reads as something to
              copy-paste, not as page chrome. */}
          <div
            style={{
              marginTop: 16,
              borderRadius: "var(--fx-r-md, 12px)",
              background: "var(--fx-s-inverse-bg)",
              color: "var(--fx-s-inverse-fg)",
              padding: "14px 16px",
              boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "var(--fx-ff-mono)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "color-mix(in srgb, var(--fx-paper) 60%, transparent)",
                }}
              >
                <Mail aria-hidden style={{ width: 14, height: 14 }} />
                {subjectLabel}
              </div>
              <CopyChip
                copied={copiedField === "subject"}
                onCopy={() => void handleCopyField("subject", subject)}
                copyLabel={t(
                  "onboarding.providerRequestTemplate.copySubject",
                  "Copiar asunto",
                )}
                copiedLabel={t(
                  "onboarding.providerRequestTemplate.copiedButton",
                  "Copiado",
                )}
              />
            </div>
            <div
              style={{
                marginTop: 8,
                fontFamily: "var(--fx-ff-serif)",
                fontSize: 16,
                lineHeight: 1.35,
              }}
            >
              {subject}
            </div>
          </div>

          {/* Body card — same lifted treatment, separate copy. */}
          <div
            style={{
              marginTop: 10,
              borderRadius: "var(--fx-r-md, 12px)",
              background: "var(--fx-s-inverse-bg)",
              color: "var(--fx-s-inverse-fg)",
              padding: "14px 16px",
              boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "var(--fx-ff-mono)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "color-mix(in srgb, var(--fx-paper) 60%, transparent)",
                }}
              >
                {bodyLabel}
              </div>
              <CopyChip
                copied={copiedField === "body"}
                onCopy={() => void handleCopyField("body", body)}
                copyLabel={t(
                  "onboarding.providerRequestTemplate.copyBody",
                  "Copiar mensaje",
                )}
                copiedLabel={t(
                  "onboarding.providerRequestTemplate.copiedButton",
                  "Copiado",
                )}
              />
            </div>
            <div
              style={{
                marginTop: 10,
                whiteSpace: "pre-line",
                fontSize: 14,
                lineHeight: 1.6,
                color: "color-mix(in srgb, var(--fx-paper) 88%, transparent)",
              }}
            >
              {/* data-mag-private: this template interpolates the company
                  name and RUT into copyable text. */}
              <span data-mag-private>{body}</span>
            </div>
          </div>

          <p
            style={{
              marginTop: 14,
              fontSize: 13,
              color: "var(--fx-ink-500, #6B7280)",
            }}
          >
            {t(
              "onboarding.providerRequestNotice.disclaimer",
              "No tenés que esperar la respuesta — seguí con la configuración. Cuando lleguen los archivos los cargás y Fiscalo migra todo.",
            )}
          </p>

          <div className="fx-onb-actions">
            <div className="side">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-secondary"
                onClick={onBack}
                disabled={isSaving}
              >
                <ArrowLeft aria-hidden />
                {t("onboarding.back", "Volver")}
              </button>
            </div>
            <div className="side end">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={isSaving}
                onClick={() => void onContinue()}
              >
                {t(
                  "onboarding.providerRequestNotice.continue",
                  "Ya lo envié, sigamos",
                )}
                <ArrowRight aria-hidden />
              </button>
            </div>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "onboarding.providerRequestNotice.brandEyebrow",
            "Tu cambio de proveedor",
          )}
          quote={
            <>
              {t(
                "onboarding.providerRequestNotice.brandQuoteLead",
                "No quedás colgado entre proveedores.",
              )}{" "}
              <span className="accent">
                {t(
                  "onboarding.providerRequestNotice.brandQuoteAccent",
                  "Hacemos la migración con vos.",
                )}
              </span>
            </>
          }
          currentStep={1}
          trailSteps={getDgiConfigTrail({
            situationChoice: "already_issuer",
            isMigratingCfes,
          })}
          valueBlock={{
            head: t(
              "onboarding.providerRequestNotice.valueHead",
              "Por qué pedir esto ahora",
            ),
            items: [
              t(
                "onboarding.providerRequestNotice.valueItem1",
                "Algunos proveedores tardan días en responder",
              ),
              t(
                "onboarding.providerRequestNotice.valueItem2",
                "Si pedís ahora, llega mientras Fiscalo se configura",
              ),
              t(
                "onboarding.providerRequestNotice.valueItem3",
                "Sin cortes en tu facturación durante el cambio",
              ),
            ],
          }}
        />
      }
    />
  );
}

interface CopyChipProps {
  copied: boolean;
  onCopy: () => void;
  copyLabel: string;
  copiedLabel: string;
}

/**
 * Small sage-accent button that swaps to a check + "Copiado" state for ~2.5s
 * after click. Pinned to the right of each card's header so a user scanning
 * the cards can copy subject or body independently without scrolling.
 */
function CopyChip({
  copied,
  onCopy,
  copyLabel,
  copiedLabel,
}: CopyChipProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={copied ? copiedLabel : copyLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 10px",
        borderRadius: 999,
        fontFamily: "var(--fx-ff-sans)",
        fontSize: 12,
        fontWeight: 500,
        border: copied
          ? "1px solid var(--fx-accent-300)"
          : "1px solid color-mix(in srgb, var(--fx-paper) 22%, transparent)",
        background: copied
          ? "var(--fx-accent-300)"
          : "color-mix(in srgb, var(--fx-paper) 8%, transparent)",
        color: copied ? "var(--fx-ink-900)" : "var(--fx-paper)",
        cursor: "pointer",
        transition: "background 120ms ease, color 120ms ease",
      }}
    >
      {copied ? (
        <Check aria-hidden style={{ width: 13, height: 13 }} strokeWidth={2.5} />
      ) : (
        <Copy aria-hidden style={{ width: 13, height: 13 }} />
      )}
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}
